/**
 * Utility functions for tracking and calculating membership earnings
 */

/**
 * Calculate total months earned from referrals and ratings
 * @param {Array} referrals - Array of referral objects with status
 * @param {Array} ratingsGiven - Array of rating objects
 * @param {number} ratingsPerMonth - Number of ratings needed per month (default: 3)
 * @returns {number} Total months earned
 */
export const calculateEarnedMonths = (referrals = [], ratingsGiven = [], ratingsPerMonth = 3) => {
  // Count completed referrals (1 month each)
  const referralMonths = referrals.filter(ref => ref.status === 'completed').length;
  
  // Calculate months from ratings (e.g., 3 ratings = 1 month)
  const ratingMonths = Math.floor(ratingsGiven.length / ratingsPerMonth);
  
  return referralMonths + ratingMonths;
};

/**
 * Check if user qualifies for Plus membership
 * @param {Object} earnings - Earnings object with referrals and ratingsGiven
 * @returns {boolean} True if user has earned at least 1 month
 */
export const shouldGrantPlusMembership = (earnings) => {
  const monthsEarned = calculateEarnedMonths(
    earnings.referrals || [],
    earnings.ratingsGiven || []
  );
  return monthsEarned >= 1;
};

/**
 * Generate unique referral code/link for a user
 * @param {string} userId - User ID
 * @returns {string} Referral link
 */
export const generateReferralLink = (userId) => {
  // In production, this would be the actual domain
  const baseUrl = window.location.origin || 'https://outback-exchange.com';
  return `${baseUrl}/signup?ref=${userId}`;
};

/**
 * Calculate progress toward next month for ratings
 * @param {Array} ratingsGiven - Array of rating objects
 * @param {number} ratingsPerMonth - Number of ratings needed per month (default: 3)
 * @returns {Object} Progress object with current, needed, and percentage
 */
export const calculateRatingProgress = (ratingsGiven = [], ratingsPerMonth = 3) => {
  const current = ratingsGiven.length % ratingsPerMonth;
  const needed = ratingsPerMonth - current;
  const percentage = (current / ratingsPerMonth) * 100;
  
  return {
    current,
    needed,
    percentage,
    totalRatings: ratingsGiven.length,
  };
};

/**
 * Calculate days left in trial period from user data
 * @param {Object} user - User object with trialEndDate
 * @returns {number} Days left in trial (defaults to 14 if no user data)
 */
export const calculateTrialDaysLeft = (user) => {
  if (!user || !user.trialEndDate) {
    return 14; // Default fallback
  }
  const now = new Date();
  const endDate = new Date(user.trialEndDate);
  const diffTime = endDate - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays); // Don't return negative days
};

/**
 * Extract referral ID from URL or return null
 * @param {string} url - Current URL or window.location.href
 * @returns {string|null} Referral user ID or null
 */
export const extractReferralId = (url = null) => {
  if (typeof window === 'undefined') return null;
  const urlToCheck = url || window.location.href;
  const urlObj = new URL(urlToCheck);
  return urlObj.searchParams.get('ref');
};

/**
 * Mark a referral as completed and grant credit
 * @param {Object} user - User object with membershipEarnings
 * @param {string} friendId - ID of the friend who signed up
 * @returns {Object} Updated user object
 */
export const completeReferral = (user, friendId) => {
  if (!user || !user.membershipEarnings) {
    return user;
  }

  const referrals = user.membershipEarnings.referrals || [];
  const existingReferral = referrals.find(ref => ref.friendId === friendId);
  
  if (existingReferral && existingReferral.status === 'completed') {
    // Already completed
    return user;
  }

  const updatedReferrals = existingReferral
    ? referrals.map(ref => 
        ref.friendId === friendId 
          ? { ...ref, status: 'completed', completedDate: new Date().toISOString() }
          : ref
      )
    : [
        ...referrals,
        {
          friendId,
          date: new Date().toISOString(),
          status: 'completed',
          completedDate: new Date().toISOString(),
        },
      ];

  const updatedEarnings = {
    ...user.membershipEarnings,
    referrals: updatedReferrals,
    monthsEarned: calculateEarnedMonths(updatedReferrals, user.membershipEarnings.ratingsGiven || []),
  };

  return {
    ...user,
    membershipEarnings: updatedEarnings,
  };
};

